import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import api from '../../services/api';

const LoadingSpinner = () => (
  <div
    role="status"
    style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
      width: '100%',
      backgroundColor: '#f9f9f9'
    }}
  >
    <div
      style={{
        width: '50px',
        height: '50px',
        border: '5px solid #e0e0e0',
        borderTop: '5px solid #2f2f2f',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }}
      aria-hidden="true"
    />
    <div style={{
      color: '#666',
      fontSize: '16px'
    }}>
      Carregando dados do mapa...
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div
    style={{
      padding: '20px',
      margin: '20px',
      backgroundColor: '#f0f0f0',
      color: '#1a1a1a',
      borderRadius: '4px',
      textAlign: 'center',
    }}
  >
    <h3>Error loading data</h3>
    <p>{message}</p>
  </div>
);

const HeatmapMG = ({ ano = "consolidado", statusGestante = "todas" }) => {
  const svgRef = useRef();
  const [geoData, setGeoData] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const [geoModule, apiResponse] = await Promise.all([
          import('./geojs-31-mun.json'),
          api.get('/relatorios/mapa-de-calor', { params: { ano, status_gestante: statusGestante } })
        ]);

        const raw = apiResponse?.data?.data ?? apiResponse?.data ?? [];
        const transformed = Array.isArray(raw)
          ? raw.map((item) => ({
              id: String(item.cod_ibge),
              name: item.municipio,
              deliveredKits: item.quantidade_kits ?? item.quantidadeKits ?? 0,
            }))
          : [];

        setGeoData(geoModule.default);
        setData(transformed);
        setError(null);
      } catch (err) {
        setError(err.message || 'Erro ao carregar dados do mapa');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [ano, statusGestante]);

  useEffect(() => {
    if (!geoData || !data || loading) return;

    const svg = d3.select(svgRef.current);
    const width = 800;
    const height = 800;

    svg.selectAll('*').remove();

    const projection = d3.geoMercator().fitSize([width, height], geoData);

    const path = d3.geoPath().projection(projection);

    const values = data.map((d) => d.deliveredKits);
    const maxValue = d3.max(values) || 1;
    const colorScale = d3
      .scaleSequential(d3.interpolateGreys)
      .domain([0, maxValue]);

    const strokeForFill = (fillColor) => {
      try {
        const h = d3.hsl(fillColor);
        return h.l > 0.6 ? '#222' : '#fff';
      } catch (e) {
        return '#fff';
      }
    };

    const map = svg.append('g').attr('class', 'map');

    const munGroups = map
      .selectAll('g.municipality')
      .data(geoData.features)
      .enter()
      .append('g')
      .attr('class', 'municipality');

    munGroups
      .append('path')
      .attr('d', path)
      .attr('fill', (d) => {
        const municipality = data.find((item) => item.id === d.properties.id);
        return municipality
          ? colorScale(municipality.deliveredKits)
          : '#e0e0e0';
      })
      .attr('stroke', function (d) {
        const municipality = data.find((item) => item.id === d.properties.id);
        const fill = municipality
          ? colorScale(municipality.deliveredKits)
          : '#e0e0e0';
        return strokeForFill(fill);
      })
      .attr('stroke-width', 0.6)
      .style('cursor', 'pointer')
      .attr('tabindex', 0)
      .on('mouseover', function (event, d) {
        const g = d3.select(this.parentNode);
        const p = d3.select(this);
        g.raise();
        const [cx, cy] = path.centroid(d);
        g.transition()
          .duration(200)
          .ease(d3.easeCubicOut)
          .attr(
            'transform',
            `translate(${cx},${cy}) scale(1.12) translate(${-cx},${-cy})`
          );
        p.transition().duration(200).attr('stroke-width', 1.8);
      })
      .on('mouseout', function (event, d) {
        const g = d3.select(this.parentNode);
        const p = d3.select(this);
        g.transition().duration(200).attr('transform', null);
        p.transition().duration(200).attr('stroke-width', 0.6);
      })
      .on('focus', function (event, d) {
        d3.select(this).dispatch('mouseover');
      })
      .on('blur', function (event, d) {
        d3.select(this).dispatch('mouseout');
      })
      .on('touchstart', function (event, d) {
        event.preventDefault();
        d3.select(this).dispatch('mouseover');
      })
      .on('touchend', function (event, d) {
        d3.select(this).dispatch('mouseout');
      })
      .append('title')
      .text((d) => {
        const municipality = data.find((item) => item.id === d.properties.id);
        return `${d.properties.name}: ${
          municipality
            ? municipality.deliveredKits + ' kits entregues'
            : 'Sem dados'
        }`;
      });

    const zoom = d3
      .zoom()
      .scaleExtent([0.6, 8])
      .on('zoom', (event) => {
        map.attr('transform', event.transform);
      });

    svg.call(zoom);

    const bounds = path.bounds(geoData);
    const dx = bounds[1][0] - bounds[0][0];
    const dy = bounds[1][1] - bounds[0][1];
    const x = (bounds[0][0] + bounds[1][0]) / 2;
    const y = (bounds[0][1] + bounds[1][1]) / 2;
    const scaleFactor = 0.95; 
    let k = Math.min(width / dx, height / dy) * scaleFactor;
    if (!isFinite(k) || k <= 0) k = 1;
    const tx = width / 2 - k * x;
    const ty = height / 2 - k * y;

    svg
      .transition()
      .duration(300)
      .call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(k));

    // Legend
    const legend = svg.append('g').attr('transform', 'translate(20, 20)');

    const gradient = legend
      .append('defs')
      .append('linearGradient')
      .attr('id', 'gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '0%');

    [0, 0.5, 1].forEach((stop) => {
      gradient
        .append('stop')
        .attr('offset', `${stop * 100}%`)
        .attr('stop-color', colorScale(stop * maxValue));
    });

    legend
      .append('rect')
      .attr('width', 200)
      .attr('height', 20)
      .style('fill', 'url(#gradient)');

    legend
      .append('text')
      .attr('x', 0)
      .attr('y', 40)
      .text('0')
      .style('font-size', '12px');

    legend
      .append('text')
      .attr('x', 200)
      .attr('y', 40)
      .text(maxValue)
      .style('text-anchor', 'end')
      .style('font-size', '12px');
  }, [data, loading]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!data) {
    return <ErrorMessage message="No data available" />;
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '800px',
        margin: '0 auto',
        textAlign: 'center',
      }}
    >
      <svg
        ref={svgRef}
        width={800}
        height={800}
        style={{ border: '1px solid #ccc', background: '#f9f9f9' }}
        role="img"
        aria-label="Mapa de calor de kits entregues por município em Minas Gerais"
      />
    </div>
  );
};

export default HeatmapMG;
